import { Injectable, OnDestroy } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';
import { ServiceDestroyed, TakeUntilDestroy } from '../decorators/take-until-destroy';
import { compareManifest } from '../helpers/version-manifest';
import { NewVersionService, RECOVERY_ATTEMPTS_KEY, RECOVERY_WINDOW_MS } from './new-version.service';
import { LocalStorageService } from './local-storage.service';
import { environment } from '../../../environments/environment';

/** Ta sama kadencja co sprawdzanie przez SwUpdate — patrz VERSION_CHECK_INTERVAL_MS. */
export const VERSION_GUARD_INTERVAL_MS = 5 * 60 * 1000;

const MANIFEST_URL = '/ngsw.json';

/** Delay before the first check — gives the service worker time to run its own update. */
export const VERSION_GUARD_START_DELAY_MS = 15 * 60 * 1000;

/** How many consecutive `stale` reads are required before the guard offers recovery. */
export const STALE_CONFIRMATIONS_REQUIRED = 3;

/** After this many failed recoveries within the time window the guard goes silent to avoid looping the app. */
export const MAX_RECOVERY_ATTEMPTS = 2;

@Injectable({
  providedIn: 'root',
})
@TakeUntilDestroy
export class VersionGuardService implements ServiceDestroyed, OnDestroy {
  serviceDestroyed = new Subject<void>();

  private ambiguousReported = false;

  private recoveryExhaustedReported = false;

  private started = false;

  private staleConfirmations = 0;

  constructor(private newVersionService: NewVersionService, private localStorageService: LocalStorageService) {}

  ngOnDestroy() {
    this.serviceDestroyed.next();
    this.serviceDestroyed.complete();
  }

  start(): void {
    if (this.started || !environment.production) {
      return;
    }
    this.started = true;

    timer(VERSION_GUARD_START_DELAY_MS, VERSION_GUARD_INTERVAL_MS)
      .pipe(takeUntil(this.serviceDestroyed))
      .subscribe(() => this.check());
  }

  async check(): Promise<void> {
    const hashTable = await this.fetchHashTable();
    if (!hashTable) {
      return;
    }

    const loadedScriptUrls = Array.from(document.scripts)
      .map(script => script.src)
      .filter(Boolean);

    const result = compareManifest(hashTable, loadedScriptUrls);

    switch (result.status) {
      case 'stale':
        this.handleStale(result.loadedMain, result.serverMain);
        break;
      case 'ambiguous':
        this.reportAmbiguousManifest(result.mainCount ?? 0);
        break;
      case 'ok':
        this.staleConfirmations = 0;
        this.clearRecoveryAttempts();
        break;
      default:
        // status 'unknown' — not enough data to compare, leave the recovery attempt counter alone
        break;
    }
  }

  private async fetchHashTable(): Promise<Record<string, string> | null> {
    try {
      const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }
      const manifest = await response.json();
      return manifest?.hashTable ?? null;
    } catch (error) {
      console.warn('Version guard could not read the manifest:', error);
      return null;
    }
  }

  /**
   * The guard is a fallback mechanism. It defers while the service worker handles
   * the update itself, requires several consistent reads, and goes silent when recovery
   * does not help — otherwise it would turn a cheap update into a full cache wipe or loop the app.
   */
  private handleStale(loadedMain: string, serverMain: string): void {
    if (this.newVersionService.isServiceWorkerHandlingUpdate()) {
      this.staleConfirmations = 0;
      return;
    }

    this.staleConfirmations += 1;
    if (this.staleConfirmations < STALE_CONFIRMATIONS_REQUIRED) {
      return;
    }

    if (!this.canAttemptRecovery()) {
      this.reportRecoveryExhausted(loadedMain, serverMain);
      return;
    }

    console.warn(`Stale version detected: loaded ${loadedMain}, server has ${serverMain}`);
    this.newVersionService.openDialog(true);
  }

  private canAttemptRecovery(): boolean {
    const stored = this.localStorageService.getItem<{ count: number; since: number }>(RECOVERY_ATTEMPTS_KEY);
    if (!stored || typeof stored.since !== 'number') {
      return true;
    }
    if (Date.now() - stored.since > RECOVERY_WINDOW_MS) {
      return true;
    }
    return (stored.count ?? 0) < MAX_RECOVERY_ATTEMPTS;
  }

  private clearRecoveryAttempts(): void {
    if (this.localStorageService.getItem(RECOVERY_ATTEMPTS_KEY)) {
      this.localStorageService.removeItem(RECOVERY_ATTEMPTS_KEY);
    }
  }

  /** Recovery is not helping — go silent and report instead of feeding a reload loop. */
  private reportRecoveryExhausted(loadedMain: string, serverMain: string): void {
    if (this.recoveryExhaustedReported) {
      return;
    }
    this.recoveryExhaustedReported = true;

    const message = `Version recovery is not helping: loaded ${loadedMain}, server has ${serverMain}`;
    console.error(message);
    Sentry.captureMessage(message, 'error');
  }

  /**
   * A correct build's manifest has exactly one main.*.js. More than one means dist
   * was not cleaned before the build — i.e. a deleteOutputPath regression. Reported
   * once per session to avoid flooding Sentry.
   */
  private reportAmbiguousManifest(mainCount: number): void {
    if (this.ambiguousReported) {
      return;
    }
    this.ambiguousReported = true;

    const message = `ngsw.json lists ${mainCount} main.*.js files — dist was not cleaned before the build`;
    console.error(message);
    Sentry.captureMessage(message, 'warning');
  }
}
